import { FC } from "react";
import { CircleArrowRight } from "lucide-react";

import { Campaign, CategoryTrees } from "@vviinn/user-managment-client-ts";
import { AssignedCategories } from "../category";
import { MEDIUM_ICON_SIZE_STYLES } from "../../ui/styles";
import { Badge } from "../../ui/layout/molecules/Badge/Badge";

type CampaignTileFooterProps = {
  campaign: Campaign;
  categories?: CategoryTrees;
};

const CONTAINER_STYLES = "mt-4 flex flex-row flex-wrap gap-2";
const VPC_CONTAINER_STYLES = "flex w-[calc(50%-20px)] flex-wrap gap-2";

export const CampaignTileFooter: FC<CampaignTileFooterProps> = ({
  campaign,
  categories,
}) => {
  const isVPD = campaign.type === "VPD";

  const attributes = [
    ...(campaign.productAttributes ?? []),
    ...(campaign.imageAttributes ?? []),
  ];

  const targetCategories = campaign?.linkedCategories?.target;
  const sourceCategories = campaign?.linkedCategories?.source;

  const googleTargetCategories = targetCategories?.googleFeedCategories;
  const googleSourceCategories = sourceCategories?.googleFeedCategories;
  const productTargetCategories = targetCategories?.productTypeCategories;
  const productSourceCategories = sourceCategories?.productTypeCategories;

  const googleTargetItems = !!googleTargetCategories?.length;
  const googleSourceItems = !!googleSourceCategories?.length;
  const productTargetItems = !!productTargetCategories?.length;
  const productSourceItems = !!productSourceCategories?.length;

  const productCategoriesList = categories?.productTypeCategory;
  const googleCategoriesList = categories?.googleFeedCategory;

  const attributeSpan = (attr: string) => <Badge type="neutral" title={attr} />;

  if (isVPD)
    return (
      <div className={CONTAINER_STYLES}>
        {attributes.length > 0 ? (
          attributes.map((attr) => (
            <Badge key={attr} type="neutral" title={attr} />
          ))
        ) : (
          <Badge type="warning" title="Predictable attributes not assigned" />
        )}
      </div>
    );

  return (
    <div className={CONTAINER_STYLES}>
      {!googleTargetItems &&
        !googleSourceItems &&
        !productTargetItems &&
        !productSourceItems &&
        campaign.type !== "VPC" && (
          <Badge type="success" title="All products assigned" />
        )}

      {campaign.type !== "VPC" && (
        <>
          {productTargetItems && productCategoriesList && (
            <AssignedCategories
              assignedCategories={productTargetCategories}
              list={productCategoriesList}
            />
          )}

          {googleTargetItems && googleCategoriesList && (
            <AssignedCategories
              assignedCategories={googleTargetCategories}
              list={googleCategoriesList}
            />
          )}
        </>
      )}

      {campaign.type === "VPC" && (
        <div className="relative flex w-full items-center justify-center">
          <div className={`${VPC_CONTAINER_STYLES} justify-end`}>
            {!productSourceItems &&
              !googleSourceItems &&
              attributeSpan("All products")}

            {productSourceItems && productCategoriesList && (
              <AssignedCategories
                assignedCategories={productSourceCategories}
                list={productCategoriesList}
              />
            )}

            {googleSourceItems && googleCategoriesList && (
              <AssignedCategories
                assignedCategories={googleSourceCategories}
                list={googleCategoriesList}
              />
            )}
          </div>

          <div className="flex w-10 justify-center">
            <CircleArrowRight
              className={`text-yellow-400 dark:text-yellow-500 ${MEDIUM_ICON_SIZE_STYLES}`}
            />
          </div>

          <div className={VPC_CONTAINER_STYLES}>
            {!productTargetItems &&
              !googleTargetItems &&
              attributeSpan("All products")}

            {productTargetItems && productCategoriesList && (
              <AssignedCategories
                assignedCategories={productTargetCategories}
                list={productCategoriesList}
                isSourceDataStyle={false}
              />
            )}

            {googleTargetItems && googleCategoriesList && (
              <AssignedCategories
                assignedCategories={googleTargetCategories}
                list={googleCategoriesList}
                isSourceDataStyle={false}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
